/** Mini UI mockups for help guidance — reference wireframes and policy lifecycle flows. */

function FlowBox({ children, tone = "neutral", darkMode, className = "" }) {
  const tones = {
    neutral: darkMode ? "bg-[#2C2C2C] border-[#3C3C4C] text-gray-100" : "bg-white border-gray-200 text-gray-800",
    accent: "bg-redhalo-mint/40 border-redhalo-red/50 text-redhalo-navy dark:text-redhalo-mint",
    warn: darkMode ? "bg-amber-950/40 border-amber-800 text-amber-100" : "bg-amber-50 border-amber-300 text-amber-950",
    danger: darkMode ? "bg-red-950/40 border-red-800 text-red-100" : "bg-red-50 border-red-300 text-red-950",
    ok: darkMode ? "bg-emerald-950/40 border-emerald-800 text-emerald-100" : "bg-emerald-50 border-emerald-300 text-emerald-950",
  };
  return (
    <div className={`rounded border px-2 py-1.5 text-[8px] leading-snug ${tones[tone]} ${className}`}>
      {children}
    </div>
  );
}

function FlowArrow({ darkMode }) {
  return (
    <div className={`flex justify-center py-0.5 text-[10px] ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
      ↓
    </div>
  );
}

function PolicyDeactivateDeleteFlowchart({ policyLabel, inFlightHint, darkMode }) {
  return (
    <div className="mt-3 space-y-0">
      <div className={`text-[9px] font-semibold uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
        Deactivate vs Delete
      </div>
      <FlowBox darkMode={darkMode}>Policy row → ⋮ menu</FlowBox>
      <FlowArrow darkMode={darkMode} />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <FlowBox tone="accent" darkMode={darkMode}>
            <strong>Deactivate</strong>
            <div className="mt-0.5 opacity-90">Status → inactive</div>
          </FlowBox>
          <FlowArrow darkMode={darkMode} />
          <FlowBox tone="ok" darkMode={darkMode}>
            No new {policyLabel} cases match this policy
          </FlowBox>
          <FlowArrow darkMode={darkMode} />
          <FlowBox darkMode={darkMode}>In-flight requests keep running on saved snapshot</FlowBox>
        </div>
        <div>
          <FlowBox tone="danger" darkMode={darkMode}>
            <strong>Delete</strong>
            <div className="mt-0.5 opacity-90">Permanent removal</div>
          </FlowBox>
          <FlowArrow darkMode={darkMode} />
          <FlowBox tone="warn" darkMode={darkMode}>
            Check in-flight requests
            <div className="mt-0.5 opacity-90">{inFlightHint}</div>
          </FlowBox>
          <FlowArrow darkMode={darkMode} />
          <FlowBox tone="danger" darkMode={darkMode}>
            Pending &gt; 0 → blocked
            <div className="mt-0.5">Pending count + by approver · Action Insights</div>
          </FlowBox>
          <FlowArrow darkMode={darkMode} />
          <FlowBox tone="ok" darkMode={darkMode}>Pending = 0 → confirm → policy removed</FlowBox>
        </div>
      </div>
    </div>
  );
}

export default function HelpUiSnippet({ variant, darkMode = false }) {
  const shell = darkMode ? "border-[#3C3C4C] bg-[#1a1a1a]" : "border-gray-200 bg-gray-50";
  const panel = darkMode ? "bg-[#2C2C2C] border-[#3C3C4C]" : "bg-white border-gray-200";
  const muted = darkMode ? "text-gray-400" : "text-gray-500";
  const text = darkMode ? "text-gray-100" : "text-gray-800";
  const accent = "bg-redhalo-mint/40 border-redhalo-red/50 text-redhalo-navy dark:text-redhalo-mint";

  const snippets = {
    dashboard: (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-semibold uppercase ${muted}`}>Dashboard</div>
        <div className="grid grid-cols-3 gap-2">
          {["Headcount", "Active", "New Joiners"].map((l) => (
            <div key={l} className={`rounded border p-2 ${panel}`}>
              <div className={`text-[9px] ${muted}`}>{l}</div>
              <div className={`mt-1 text-sm font-bold ${text}`}>—</div>
            </div>
          ))}
        </div>
      </div>
    ),
    "employees-list": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className="mb-2 flex gap-2">
          <div className={`h-7 flex-1 rounded-full border px-2 text-[9px] leading-7 ${panel} ${muted}`}>
            Search · Filters
          </div>
          <div className={`rounded-full border px-2 py-1 text-[9px] font-semibold ${accent}`}>
            + Create
          </div>
        </div>
        <div className={`mb-1 grid grid-cols-4 gap-1 px-1 text-[8px] ${muted}`}>
          <span>Code</span>
          <span>Name</span>
          <span>Status</span>
          <span>Profile %</span>
        </div>
        <div className={`rounded border ${panel}`}>
          {["PSS-020", "005209"].map((code) => (
            <div
              key={code}
              className={`flex justify-between border-b px-2 py-1 text-[9px] last:border-0 ${text}`}
            >
              <span>{code}</span>
              <span className={muted}>⋮ Actions</span>
            </div>
          ))}
        </div>
      </div>
    ),
    "employee-profile-sections": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Profile tabs</div>
        <div className="flex flex-wrap gap-1">
          {["Org", "Personal", "Family", "Bank", "Education", "Profession", "Statutory", "Documents"].map(
            (t, i) => (
              <span
                key={t}
                className={`rounded px-1.5 py-0.5 text-[8px] ${
                  i === 0 ? accent : `${panel} border ${muted}`
                }`}
              >
                {t}
              </span>
            )
          )}
        </div>
        <div className={`mt-2 text-[9px] ${muted}`}>Profile completion: sections checklist</div>
      </div>
    ),
    "create-employee": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Create Employee (Direct)</div>
        <div className={`rounded border p-2 text-[9px] ${panel} ${muted}`}>
          Full form · Auto/manual code · Save → live employee
        </div>
      </div>
    ),
    "onboard-employee": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-1 text-[10px] font-bold ${text}`}>Onboard Employee</div>
        <div className={`text-[9px] ${muted}`}>Draft candidate · Policy-matched · Insights linked</div>
        <div className={`mt-2 rounded border border-dashed p-2 text-[8px] ${panel} ${muted}`}>
          Org tab first · Policy from Settings → Onboarding
        </div>
      </div>
    ),
    "employee-lifecycle": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 flex gap-1 text-[9px] font-bold ${text}`}>
          <span className={`rounded-full border px-2 py-0.5 ${panel}`}>Employees</span>
          <span className={`rounded-full px-2 py-0.5 ${accent}`}>Actions</span>
        </div>
        {["View Employee", "Generate Letter", "Confirmation", "Transfer", "Resignation"].map((a) => (
          <div key={a} className={`mb-1 rounded border px-2 py-1 text-[9px] ${panel} ${text}`}>
            {a}
          </div>
        ))}
      </div>
    ),
    "lifecycle-approvals-desk": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Lifecycle flow</div>
        <div className="space-y-1">
          {["1. Policy (Settings)", "2. Raise (Employee)", "3. Request (status)", "4. Approvals (act)", "5. Insights (admin)"].map(
            (step) => (
              <div key={step} className={`rounded border px-2 py-1 text-[8px] ${panel} ${text}`}>
                {step}
              </div>
            )
          )}
        </div>
        <div className={`mt-2 rounded border border-dashed px-2 py-1 text-[8px] ${muted}`}>
          Quick Panel right rail · Admin + ESS
        </div>
      </div>
    ),
    "employee-insights": (
      <div className={`flex gap-2 rounded-lg border p-2 ${shell}`}>
        <div className={`w-16 shrink-0 space-y-1 rounded border p-1.5 ${panel}`}>
          <div className={`text-[7px] font-semibold uppercase ${muted}`}>Nav</div>
          <div className={`rounded px-1 py-0.5 text-[8px] ${muted}`}>Help</div>
          <div className={`mt-1 rounded border px-1 py-1 text-[8px] font-semibold ${accent}`}>
            Insights
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex justify-between">
            <span className={`text-[10px] font-bold ${text}`}>Employee Insights</span>
            <span className={`rounded-full border px-2 py-0.5 text-[8px] ${accent}`}>Panel</span>
          </div>
          <div className={`rounded border px-2 py-1 text-[9px] ${panel} ${text}`}>
            Onboarding · Checklist · Leave-style key fields
          </div>
        </div>
      </div>
    ),
    import: (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Import Employee Data</div>
        <div className={`rounded border border-dashed p-4 text-center text-[9px] ${panel} ${muted}`}>
          Template download · Upload Excel · Row validation
        </div>
      </div>
    ),
    reports: (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Reports submenu</div>
        {[
          "Employee",
          "Age",
          "Service",
          "Salary",
          "Confirmation",
          "Transfers",
          "Resignations",
        ].map((r) => (
          <div key={r} className={`mb-1 rounded border px-2 py-1 text-[9px] ${panel} ${text}`}>
            {r} Report
          </div>
        ))}
      </div>
    ),
    export: (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Export sections</div>
        {["Org", "Personal", "Family", "Bank", "Education", "Professional", "Statutory", "All"].map(
          (e) => (
            <div
              key={e}
              className={`mb-1 flex justify-between rounded border px-2 py-1 text-[9px] ${panel}`}
            >
              <span className={text}>{e}</span>
              <span className={accent}>↓</span>
            </div>
          )
        )}
      </div>
    ),
    "settings-general": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>General Settings</div>
        <div className={`rounded border p-2 text-[9px] ${panel}`}>
          <div className={text}>Enable ESS: Yes / No</div>
          <div className={`mt-1 ${muted}`}>☑ Enable ESS by default (Send Invite)</div>
          <div className={`mt-2 text-right ${accent} inline-block rounded px-2 py-0.5 text-[8px]`}>
            Save
          </div>
        </div>
      </div>
    ),
    "settings-employee-code": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Employee Code Structure</div>
        <div className="flex gap-2">
          {["Manual", "Automated"].map((m, i) => (
            <span
              key={m}
              className={`rounded border px-2 py-1 text-[9px] ${i === 1 ? accent : `${panel} ${muted}`}`}
            >
              {m}
            </span>
          ))}
        </div>
        <div className={`mt-2 text-[8px] ${muted}`}>Prefix · Digits · Next preview · View History</div>
      </div>
    ),
    "settings-policies-onboarding": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Onboarding Policy wizard</div>
        <div className="flex flex-wrap gap-1">
          {["Basics", "Profile", "Workflow", "Letters", "Checklist", "Applicability"].map((s, i) => (
            <span
              key={s}
              className={`rounded px-1.5 py-0.5 text-[8px] ${
                i === 0 ? accent : `${panel} border ${muted}`
              }`}
            >
              {i + 1}. {s}
            </span>
          ))}
        </div>
        <div className={`mt-2 text-[8px] ${muted}`}>
          Auto approved / Workflow · Checklist mapping · Org applicability
        </div>
        <PolicyDeactivateDeleteFlowchart
          darkMode={darkMode}
          policyLabel="onboarding"
          inFlightHint="Draft · Pending approval"
        />
      </div>
    ),
    "settings-policies-confirmation": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Confirmation Policy</div>
        <div className={`text-[9px] ${muted}`}>Basics → Rules → Workflow → Letters → Checklist → Applicability</div>
        <PolicyDeactivateDeleteFlowchart
          darkMode={darkMode}
          policyLabel="confirmation"
          inFlightHint="Pending approval · On hold"
        />
      </div>
    ),
    "settings-policies-transfer": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Transfer Policy</div>
        <div className={`text-[9px] ${muted}`}>Basics → Transfer rules → Workflow → Letters → Checklist → Applicability</div>
        <PolicyDeactivateDeleteFlowchart
          darkMode={darkMode}
          policyLabel="transfer"
          inFlightHint="Draft · Pending · On hold · Cancellation pending"
        />
      </div>
    ),
    "settings-policies-separation": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Separation Policy</div>
        <div className={`text-[9px] ${muted}`}>Basics → Separation rules → Workflow → Letters → Checklist → Applicability</div>
        <PolicyDeactivateDeleteFlowchart
          darkMode={darkMode}
          policyLabel="separation"
          inFlightHint="Draft · Pending · Clearance · FnF · Cancellation pending"
        />
      </div>
    ),
    "settings-structures": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Salary Structures</div>
        <div className={`mb-1 flex gap-1 text-[8px] ${muted}`}>
          <span className={`rounded border px-1 ${panel}`}>Structures</span>
          <span className={`rounded border px-1 ${panel}`}>Statutory Rates</span>
          <span className={`rounded border px-1 font-semibold ${panel} ${text}`}>TDS Reference Heads</span>
        </div>
        <div className={`rounded border p-2 text-[9px] ${panel} ${muted}`}>
          Components (mandatory TDS head) · Templates · Employee assignment
        </div>
      </div>
    ),
    "settings-letters": (
      <div className={`rounded-lg border p-3 ${shell}`}>
        <div className={`mb-2 text-[10px] font-bold ${text}`}>Letters Management</div>
        <div className={`rounded border p-2 text-[9px] ${panel} ${muted}`}>
          Appointment · Offer · Transfer · Exit templates
        </div>
      </div>
    ),
  };

  return snippets[variant] || (
    <div className={`rounded-lg border p-3 text-[10px] ${shell} ${muted}`}>
      UI preview not available for this topic.
    </div>
  );
}
